/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * A directive for managing all active Guacamole sessions.
 */
angular.module('settings').directive('guacSettingsSessions', [function guacSettingsSessions() {
    
    return {
        // Element only
        restrict: 'E',
        replace: true,

        scope: {
        },

        templateUrl: 'app/settings/templates/settingsSessions.html',
        controller: ['$scope', '$injector', '$timeout', function settingsSessionsController($scope, $injector, $timeout) {

            // Required types
            var ActiveConnectionWrapper = $injector.get('ActiveConnectionWrapper');
            var ClientIdentifier        = $injector.get('ClientIdentifier');
            var ConnectionGroup         = $injector.get('ConnectionGroup');
            var SortOrder               = $injector.get('SortOrder');

            // Required services
            var $filter                 = $injector.get('$filter');
            var $translate              = $injector.get('$translate');
            var $q                      = $injector.get('$q');
            var $interval               = $injector.get('$interval');
            var activeConnectionService = $injector.get('activeConnectionService');
            var authenticationService   = $injector.get('authenticationService');
            var connectionGroupService  = $injector.get('connectionGroupService');
            var dataSourceService       = $injector.get('dataSourceService');
            var guacNotification        = $injector.get('guacNotification');
            var requestService          = $injector.get('requestService');
            
            // Map to store telemetry data by connection identifier
            // Store on scope so Angular can detect changes
            $scope.telemetryData = {};
            var telemetryData = $scope.telemetryData; // Keep reference for convenience

            /**
             * The identifiers of all data sources accessible by the current
             * user.
             *
             * @type String[]
             */
            var dataSources = authenticationService.getAvailableDataSources();

            /**
             * The ActiveConnectionWrappers of all active sessions accessible
             * by the current user, or null if the active sessions have not yet
             * been loaded.
             *
             * @type ActiveConnectionWrapper[]
             */
            $scope.wrappers = null;

            /**
             * SortOrder instance which maintains the sort order of the visible
             * connection wrappers.
             *
             * @type SortOrder
             */
            $scope.wrapperOrder = new SortOrder([
                'activeConnection.username',
                'startDate',
                'activeConnection.remoteHost',
                'name'
            ]);

            /**
             * Array of all wrapper properties that are filterable.
             *
             * @type String[]
             */
            $scope.filteredWrapperProperties = [
                'activeConnection.username',
                'startDate',
                'activeConnection.remoteHost',
                'name'
            ];

            /**
             * All active connections, if known, grouped by corresponding data
             * source identifier, or null if active connections have not yet
             * been loaded.
             *
             * @type Object.<String, Object.<String, ActiveConnection>>
             */
            var allActiveConnections = null;

            /**
             * Map of all visible connections by data source identifier and
             * object identifier, or null if visible connections have not yet
             * been loaded.
             *
             * @type Object.<String, Object.<String, Connection>>
             */
            var allConnections = null;

            /**
             * The date format for use for session-related dates.
             *
             * @type String
             */
            var sessionDateFormat = null;

            /**
             * Map of all currently-selected active connection wrappers by
             * data source and identifier.
             * 
             * @type Object.<String, Object.<String, ActiveConnectionWrapper>>
             */
            var allSelectedWrappers = {};

            /**
             * Adds the given connection to the internal set of visible
             * connections.
             *
             * @param {String} dataSource
             *     The identifier of the data source associated with the given
             *     connection.
             *
             * @param {Connection} connection
             *     The connection to add to the internal set of visible
             *     connections.
             */
            var addConnection = function addConnection(dataSource, connection) {

                // Add given connection to set of visible connections
                allConnections[dataSource][connection.identifier] = connection;

            };

            /**
             * Adds all descendant connections of the given connection group to
             * the internal set of connections.
             * 
             * @param {String} dataSource
             *     The identifier of the data source associated with the given
             *     connection group.
             *
             * @param {ConnectionGroup} connectionGroup
             *     The connection group whose descendant connections should be
             *     added to the internal set of connections.
             */
            var addDescendantConnections = function addDescendantConnections(dataSource, connectionGroup) {

                // Add all child connections
                angular.forEach(connectionGroup.childConnections, function addConnectionForDataSource(connection) {
                    addConnection(dataSource, connection);
                });

                // Add all child connection groups
                angular.forEach(connectionGroup.childConnectionGroups, function addConnectionGroupForDataSource(connectionGroup) {
                    addDescendantConnections(dataSource, connectionGroup);
                });

            };

            /**
             * Wraps all loaded active connections, storing the resulting array
             * within the scope. If required data has not yet finished loading,
             * this function has no effect.
             */
            var wrapAllActiveConnections = function wrapAllActiveConnections() {

                // Abort if not all required data is available
                if (!allActiveConnections || !allConnections || !sessionDateFormat)
                    return;

                // Wrap all active connections for sake of display
                $scope.wrappers = [];
                angular.forEach(allActiveConnections, function wrapActiveConnections(activeConnections, dataSource) {
                    angular.forEach(activeConnections, function wrapActiveConnection(activeConnection, identifier) {

                        // Retrieve corresponding connection
                        var connection = allConnections[dataSource][activeConnection.connectionIdentifier];

                        // Add wrapper
                        if (activeConnection.username !== null) {
                            $scope.wrappers.push(new ActiveConnectionWrapper({
                                dataSource       : dataSource,
                                name             : connection.name,
                                startDate        : $filter('date')(activeConnection.startDate, sessionDateFormat),
                                activeConnection : activeConnection
                            }));
                        }

                    });
                });

            };

            // Retrieve all connections 
            dataSourceService.apply(
                connectionGroupService.getConnectionGroupTree,
                dataSources,
                ConnectionGroup.ROOT_IDENTIFIER
            )
            .then(function connectionGroupsReceived(rootGroups) {

                allConnections = {};

                // Load connections from each received root group
                angular.forEach(rootGroups, function connectionGroupReceived(rootGroup, dataSource) {
                    allConnections[dataSource] = {};
                    addDescendantConnections(dataSource, rootGroup);
                });

                // Attempt to produce wrapped list of active connections
                wrapAllActiveConnections();

            }, requestService.DIE);
            
            // Query active sessions
            dataSourceService.apply(
                activeConnectionService.getActiveConnections,
                dataSources
            )
            .then(function sessionsRetrieved(retrievedActiveConnections) {

                // Store received map of active connections
                allActiveConnections = retrievedActiveConnections;

                // Attempt to produce wrapped list of active connections
                wrapAllActiveConnections();

            }, requestService.DIE);

            // Get session date format
            $translate('SETTINGS_SESSIONS.FORMAT_STARTDATE').then(function sessionDateFormatReceived(retrievedSessionDateFormat) {

                // Store received date format
                sessionDateFormat = retrievedSessionDateFormat;

                // Attempt to produce wrapped list of active connections
                wrapAllActiveConnections();

            }, angular.noop);

            /**
             * Returns whether critical data has completed being loaded.
             *
             * @returns {Boolean}
             *     true if enough data has been loaded for the user interface
             *     to be useful, false otherwise.
             */
            $scope.isLoaded = function isLoaded() {
                return $scope.wrappers !== null;
            };

            /**
             * An action to be provided along with the object sent to
             * showStatus which closes the currently-shown status dialog.
             */
            var CANCEL_ACTION = {
                name        : "SETTINGS_SESSIONS.ACTION_CANCEL",
                // Handle action
                callback    : function cancelCallback() {
                    guacNotification.showStatus(false);
                }
            };
            
            /**
             * An action to be provided along with the object sent to
             * showStatus which immediately deletes the currently selected
             * sessions.
             */
            var DELETE_ACTION = {
                name        : "SETTINGS_SESSIONS.ACTION_DELETE",
                className   : "danger",
                // Handle action
                callback    : function deleteCallback() {
                    deleteAllSessionsImmediately();
                    guacNotification.showStatus(false);
                }
            };
            
            /**
             * Immediately deletes the selected sessions, without prompting the
             * user for confirmation.
             */
            var deleteAllSessionsImmediately = function deleteAllSessionsImmediately() {

                var deletionRequests = [];

                // Perform deletion for each relevant data source
                angular.forEach(allSelectedWrappers, function deleteSessionsImmediately(selectedWrappers, dataSource) {

                    // Delete sessions, if any are selected
                    var identifiers = Object.keys(selectedWrappers);
                    if (identifiers.length)
                        deletionRequests.push(activeConnectionService.deleteActiveConnections(dataSource, identifiers));

                });

                // Update interface
                $q.all(deletionRequests)
                .then(function activeConnectionsDeleted() {

                    // Remove deleted connections from wrapper array
                    $scope.wrappers = $scope.wrappers.filter(function activeConnectionStillExists(wrapper) {
                        return !(wrapper.activeConnection.identifier in (allSelectedWrappers[wrapper.dataSource] || {}));
                    });

                    // Clear selection
                    allSelectedWrappers = {};

                }, guacNotification.SHOW_REQUEST_ERROR);

            }; 
            
            /**
             * Delete all selected sessions, prompting the user first to
             * confirm that deletion is desired.
             */
            $scope.deleteSessions = function deleteSessions() {
                // Confirm deletion request
                guacNotification.showStatus({
                    'title'      : 'SETTINGS_SESSIONS.DIALOG_HEADER_CONFIRM_DELETE',
                    'text'       : {
                        'key' : 'SETTINGS_SESSIONS.TEXT_CONFIRM_DELETE'
                    },
                    'actions'    : [ DELETE_ACTION, CANCEL_ACTION]
                });
            };

            /**
             * Returns the relative URL of the client page which accesses the
             * given active connection. If the active connection is not
             * connectable, null is returned.
             *
             * @param {String} dataSource
             *     The unique identifier of the data source containing the
             *     active connection.
             *
             * @param {String} activeConnection
             *     The active connection to determine the relative URL of.
             *
             * @returns {String}
             *     The relative URL of the client page which accesses the given
             *     active connection, or null if the active connection is not
             *     connectable.
             */
            $scope.getClientURL = function getClientURL(dataSource, activeConnection) {

                if (!activeConnection.connectable)
                    return null;

                return '#/client/' + encodeURIComponent(ClientIdentifier.toString({
                    dataSource : dataSource,
                    type       : ClientIdentifier.Types.ACTIVE_CONNECTION,
                    id         : activeConnection.identifier
                }));

            };

            /**
             * Returns whether the selected sessions can be deleted.
             * 
             * @returns {Boolean}
             *     true if selected sessions can be deleted, false otherwise.
             */
            /**
             * Fetches telemetry data for all active sessions.
             */
            var fetchTelemetryData = function fetchTelemetryData() {
                if (!authenticationService) {
                    console.warn('[Telemetry] No authenticationService available');
                    return;
                }
                
                console.log('[Telemetry] Fetching telemetry data from API...');
                // Fetch all telemetry sessions from API
                authenticationService.request({
                    method: 'GET',
                    url: 'api/telemetry/sessions'
                }).then(function(telemetrySessions) {
                    console.log('[Telemetry] Received', telemetrySessions ? telemetrySessions.length : 0, 'sessions from API');
                    console.log('[Telemetry] Raw API response:', JSON.stringify(telemetrySessions, null, 2));
                    
                    // Index by connection ID for quick lookup
                    // Clear existing data
                    Object.keys(telemetryData).forEach(function(key) {
                        delete telemetryData[key];
                    });
                    if (angular.isArray(telemetrySessions)) {
                        angular.forEach(telemetrySessions, function(session) {
                            if (session && session.sessionId) {
                                // ALWAYS use sessionId as the primary key - no fallback to connectionId
                                var sessionId = String(session.sessionId);
                                telemetryData[sessionId] = session;
                                
                                // Also store with connectionId for backwards compatibility lookup
                                if (session.connectionId) {
                                    var connId = String(session.connectionId);
                                    telemetryData[connId] = session;
                                }
                                
                                console.log('[Telemetry] Stored session with sessionId as key:', sessionId, '| connectionId:', session.connectionId, '| Rating:', session.connectionRating, '| Score:', session.connectionRatingScore);
                            } else {
                                console.warn('[Telemetry] Skipping invalid session (missing sessionId):', session);
                            }
                        });
                    }
                    console.log('[Telemetry] telemetryData keys:', Object.keys(telemetryData));
                    console.log('[Telemetry] telemetryData size:', Object.keys(telemetryData).length);
                    console.log('[Telemetry] telemetryData contents:', telemetryData);
                    
                    // Log wrapper connection IDs for comparison
                    if ($scope.wrappers && $scope.wrappers.length > 0) {
                        console.log('[Telemetry] Wrapper connection IDs:');
                        angular.forEach($scope.wrappers, function(wrapper, idx) {
                            var wConnId = wrapper.activeConnection && (wrapper.activeConnection.connectionIdentifier || wrapper.activeConnection.identifier);
                            var wConnIdStr = wConnId ? String(wConnId) : null;
                            console.log('  Wrapper', idx + ':', wConnIdStr, '(type:', typeof wConnIdStr, ')', 
                                '| identifier:', wrapper.activeConnection && wrapper.activeConnection.identifier, 
                                '| connectionIdentifier:', wrapper.activeConnection && wrapper.activeConnection.connectionIdentifier, 
                                '| Has data:', wConnIdStr ? !!telemetryData[wConnIdStr] : false);
                            if (wConnIdStr && !telemetryData[wConnIdStr]) {
                                // Try to find a match with different case/type
                                var found = false;
                                for (var key in telemetryData) {
                                    if (String(key) === wConnIdStr || key === wConnIdStr) {
                                        console.log('    ⚠️ Found match with key:', key, 'but lookup failed');
                                        found = true;
                                        break;
                                    }
                                }
                                if (!found) {
                                    console.log('    ❌ No match found in telemetryData');
                                }
                            }
                        });
                    }
                    
                    // Also try to get data from frontend telemetry service if available
                    // This provides real-time data even if backend sync hasn't happened yet
                    try {
                        var qinayaTelemetryService = $injector.get('qinayaTelemetryService');
                        if (qinayaTelemetryService) {
                            // Use wrappers if available, otherwise we'll try again later
                            var wrappersToCheck = $scope.wrappers || [];
                            angular.forEach(wrappersToCheck, function(wrapper) {
                                if (wrapper && wrapper.activeConnection) {
                                    // Use connectionIdentifier (underlying connection ID) instead of identifier (active connection UUID)
                                    var connId = wrapper.activeConnection.connectionIdentifier || wrapper.activeConnection.identifier;
                                    if (!connId) return; // Skip this iteration
                                    
                                    // Convert to string for consistent lookup
                                    var connIdStr = String(connId);
                                    
                                    // If we don't have backend data for this connection, try frontend service
                                    if (!telemetryData[connIdStr]) {
                                        var sessionId = qinayaTelemetryService.getSessionId(connIdStr);
                                        if (!sessionId) {
                                            // No active session, skip
                                            return;
                                        }
                                        
                                        var rating = qinayaTelemetryService.getConnectionRating(connIdStr);
                                        var avgLatency = qinayaTelemetryService.getAverageLatency(connIdStr);
                                        
                                        // Only create frontend entry if we have meaningful data
                                        if (rating || avgLatency != null) {
                                            // Store with sessionId as key (primary), also store with connectionId for lookup
                                            telemetryData[sessionId] = {
                                                sessionId: sessionId,
                                                connectionId: connIdStr,
                                                connectionRating: rating ? rating.rating : null,
                                                connectionRatingScore: rating ? rating.score : null,
                                                avgLatencyMs: avgLatency,
                                                currentLatencyMs: qinayaTelemetryService.getCurrentLatency(connIdStr),
                                                minLatencyMs: qinayaTelemetryService.getMinLatency(connIdStr),
                                                maxLatencyMs: qinayaTelemetryService.getMaxLatency(connIdStr),
                                                avgJitterMs: qinayaTelemetryService.getJitter(connIdStr),
                                                packetLossPercent: qinayaTelemetryService.getPacketLossPercent(connIdStr),
                                                connectionLossesCount: qinayaTelemetryService.getConnectionLossCount(connIdStr),
                                                disconnectionCount: qinayaTelemetryService.getDisconnectionCount(connIdStr),
                                                totalBytesSent: qinayaTelemetryService.getBytesSent(connIdStr),
                                                totalBytesReceived: qinayaTelemetryService.getBytesReceived(connIdStr)
                                            };
                                            // Also store with connectionId for backwards compatibility lookup
                                            telemetryData[connIdStr] = telemetryData[sessionId];
                                        }
                                    }
                                }
                            });
                        }
                    } catch (e) {
                        // Frontend service not available - that's OK, just use backend data
                    }
                }).catch(function(error) {
                    console.error('[Telemetry] Failed to fetch telemetry data:', error);
                    // Try frontend service as fallback
                    try {
                        var qinayaTelemetryService = $injector.get('qinayaTelemetryService');
                        if (qinayaTelemetryService) {
                            var wrappersToCheck = $scope.wrappers || [];
                            // Clear existing data
                            Object.keys(telemetryData).forEach(function(key) {
                                delete telemetryData[key];
                            });
                            angular.forEach(wrappersToCheck, function(wrapper) {
                                if (wrapper && wrapper.activeConnection) {
                                    // Use connectionIdentifier (underlying connection ID) instead of identifier (active connection UUID)
                                    var connId = wrapper.activeConnection.connectionIdentifier || wrapper.activeConnection.identifier;
                                    if (!connId) return; // Skip this iteration
                                    var connIdStr = String(connId);
                                    
                                    var rating = qinayaTelemetryService.getConnectionRating(connIdStr);
                                    var avgLatency = qinayaTelemetryService.getAverageLatency(connIdStr);
                                    
                                    if (rating || avgLatency != null) {
                                        telemetryData[connIdStr] = {
                                            connectionId: connIdStr,
                                            connectionRating: rating ? rating.rating : null,
                                            connectionRatingScore: rating ? rating.score : null,
                                            avgLatencyMs: avgLatency,
                                            currentLatencyMs: qinayaTelemetryService.getCurrentLatency(connId)
                                        };
                                    }
                                }
                            });
                        }
                    } catch (e) {
                        // Frontend service not available
                    }
                });
            };
            
            /**
             * Gets the network quality status class (green/yellow/red) for a
             * given connection identifier.
             *
             * @param {String} connectionId
             *     The connection identifier.
             *
             * @returns {String}
             *     The CSS class name ('network-status-green', 'network-status-yellow',
             *     or 'network-status-red'), or empty string if unavailable.
             */
            $scope.getNetworkStatusClass = function getNetworkStatusClass(connectionId) {
                if (!connectionId) {
                    console.log('[Telemetry] getNetworkStatusClass: No connectionId provided');
                    return '';
                }
                
                // Convert to string for consistent lookup
                var connIdStr = String(connectionId);
                
                // Try backend data first
                var session = telemetryData[connIdStr];
                if (session) {
                    console.log('[Telemetry] getNetworkStatusClass found session for', connIdStr, ':', session);
                } else {
                    console.log('[Telemetry] getNetworkStatusClass NO session found for', connIdStr, '| Available keys:', Object.keys(telemetryData));
                    console.log('[Telemetry] Checking all keys and values:', Object.keys(telemetryData).map(function(k) { return k + ':' + (telemetryData[k] ? telemetryData[k].connectionId : 'null'); }));
                }
                
                // If no backend data, try frontend service
                if (!session) {
                    try {
                        var qinayaTelemetryService = $injector.get('qinayaTelemetryService');
                        if (qinayaTelemetryService) {
                            var rating = qinayaTelemetryService.getConnectionRating(connIdStr);
                            var avgLatency = qinayaTelemetryService.getAverageLatency(connIdStr);
                            var currentLatency = qinayaTelemetryService.getCurrentLatency(connIdStr);
                            
                            console.log('[Telemetry] Frontend service check for', connectionId, '| Rating:', rating, '| AvgLatency:', avgLatency, '| CurrentLatency:', currentLatency);
                            
                            // Use rating/score from frontend service
                            if (rating && rating.score != null) {
                                if (rating.score >= 60) {
                                    console.log('[Telemetry] Returning network-status-green from frontend service');
                                    return 'network-status-green';
                                }
                                if (rating.score >= 40) {
                                    console.log('[Telemetry] Returning network-status-yellow from frontend service');
                                    return 'network-status-yellow';
                                }
                                console.log('[Telemetry] Returning network-status-red from frontend service');
                                return 'network-status-red';
                            }
                            
                            // Use latency as fallback
                            var latencyToUse = currentLatency != null ? currentLatency : avgLatency;
                            if (latencyToUse != null) {
                                if (latencyToUse < 70) {
                                    console.log('[Telemetry] Returning network-status-green from latency:', latencyToUse);
                                    return 'network-status-green';
                                }
                                if (latencyToUse < 150) {
                                    console.log('[Telemetry] Returning network-status-yellow from latency:', latencyToUse);
                                    return 'network-status-yellow';
                                }
                                console.log('[Telemetry] Returning network-status-red from latency:', latencyToUse);
                                return 'network-status-red';
                            }
                        }
                    } catch (e) {
                        console.warn('[Telemetry] Frontend service not available:', e.message);
                    }
                    
                    console.log('[Telemetry] No data found, returning empty string');
                    return ''; // No data - no indicator
                }
                
                var rating = session.connectionRating || '';
                var score = session.connectionRatingScore;
                
                console.log('[Telemetry] getNetworkStatusClass evaluating - rating:', rating, 'score:', score, 'type of score:', typeof score);
                
                // Use score if available, otherwise use rating string
                if (score !== null && score !== undefined) {
                    var result = score >= 60 ? 'network-status-green' : (score >= 40 ? 'network-status-yellow' : 'network-status-red');
                    console.log('[Telemetry] getNetworkStatusClass RETURNING (from score):', result, 'for score:', score);
                    return result;
                }
                
                // Fall back to rating string
                rating = rating.toLowerCase();
                if (rating === 'excellent' || rating === 'good') {
                    console.log('[Telemetry] getNetworkStatusClass RETURNING (from rating good): network-status-green');
                    return 'network-status-green';
                }
                if (rating === 'fair') {
                    console.log('[Telemetry] getNetworkStatusClass RETURNING (from rating fair): network-status-yellow');
                    return 'network-status-yellow';
                }
                if (rating === 'poor' || rating === 'bad') {
                    console.log('[Telemetry] getNetworkStatusClass RETURNING (from rating poor): network-status-red');
                    return 'network-status-red';
                }
                
                // Last resort: use latency if available
                var latency = session.avgLatencyMs || session.currentLatencyMs;
                if (latency != null) {
                    var result = latency < 70 ? 'network-status-green' : (latency < 150 ? 'network-status-yellow' : 'network-status-red');
                    console.log('[Telemetry] getNetworkStatusClass RETURNING (from latency):', result, 'for latency:', latency);
                    return result;
                }
                
                console.log('[Telemetry] getNetworkStatusClass RETURNING: empty string (no data)');
                return '';
            };
            
            /**
             * Gets the sessionId for a connection's active session.
             * This is used to link to the performance page with the correct sessionId.
             *
             * @param {Object} activeConnection
             *     The active connection object.
             *
             * @returns {String}
             *     The sessionId, or null if not found.
             */
            $scope.getSessionIdForConnection = function getSessionIdForConnection(activeConnection) {
                if (!activeConnection) {
                    return null;
                }
                
                var connId = String(activeConnection.connectionIdentifier || activeConnection.identifier || '');
                if (!connId) {
                    return null;
                }
                
                // Find the session for this connection
                var session = telemetryData[connId];
                if (session && session.sessionId) {
                    return session.sessionId;
                }
                
                // Try to get from frontend service
                try {
                    var qinayaTelemetryService = $injector.get('qinayaTelemetryService');
                    if (qinayaTelemetryService) {
                        var sessionId = qinayaTelemetryService.getSessionId(connId);
                        if (sessionId) {
                            return sessionId;
                        }
                    }
                } catch (e) {
                    // Service not available
                }
                
                return null;
            };

            /**
             * Gets the network quality status title (tooltip) for a given
             * connection identifier.
             *
             * @param {String} connectionId
             *     The connection identifier.
             *
             * @returns {String}
             *     The tooltip text, or empty string if unavailable.
             */
            $scope.getNetworkStatusTitle = function getNetworkStatusTitle(connectionId) {
                if (!connectionId || !telemetryData[connectionId]) {
                    return '';
                }
                
                var session = telemetryData[connectionId];
                var rating = session.connectionRating;
                var score = session.connectionRatingScore;
                
                if (rating && score !== null && score !== undefined) {
                    return rating + ' (' + score + '/100)';
                }
                if (rating) {
                    return rating;
                }
                
                return '';
            };
            
            // Fetch telemetry data when wrappers are loaded/updated
            $scope.$watch('wrappers', function(newWrappers, oldWrappers) {
                if (newWrappers && newWrappers.length > 0) {
                    console.log('[Telemetry] Wrappers loaded, fetching telemetry data...');
                    fetchTelemetryData();
                }
            }, true);
            
            // Fetch telemetry data on initial load and periodically
            console.log('[Telemetry] Initializing telemetry data fetch...');
            fetchTelemetryData();
            var telemetryUpdateInterval = $interval(function() {
                console.log('[Telemetry] Periodic fetch triggered');
                fetchTelemetryData();
            }, 5000); // Update every 5 seconds
            
            // Telemetry data is now on scope, so Angular will detect changes automatically
            
            // Clean up interval on scope destroy
            $scope.$on('$destroy', function() {
                if (telemetryUpdateInterval) {
                    $interval.cancel(telemetryUpdateInterval);
                }
            });

            $scope.canDeleteSessions = function canDeleteSessions() {

                // We can delete sessions if at least one is selected
                for (var dataSource in allSelectedWrappers) {
                    for (var identifier in allSelectedWrappers[dataSource])
                        return true;
                }

                return false;

            };
            
            /**
             * Called whenever an active connection wrapper changes selected
             * status.
             * 
             * @param {ActiveConnectionWrapper} wrapper
             *     The wrapper whose selected status has changed.
             */
            $scope.wrapperSelectionChange = function wrapperSelectionChange(wrapper) {

                // Get selection map for associated data source, creating if necessary
                var selectedWrappers = allSelectedWrappers[wrapper.dataSource];
                if (!selectedWrappers)
                    selectedWrappers = allSelectedWrappers[wrapper.dataSource] = {};

                // Add wrapper to map if selected
                if (wrapper.checked)
                    selectedWrappers[wrapper.activeConnection.identifier] = wrapper;

                // Otherwise, remove wrapper from map
                else
                    delete selectedWrappers[wrapper.activeConnection.identifier];

            };
            
        }]
    };
    
}]);
